import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Button,
  Container,
  Avatar,
  Tooltip,
  useTheme,
  Menu,
  MenuItem,
  Tabs,
  Tab
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import PersonIcon from '@mui/icons-material/Person';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import { logout } from '../../store/actions/authActions';
import { checkInviteEligibility } from '../../store/actions/inviteActions';
import logger from '../../utils/logger';
import LoginModal from '../Common/LoginModal';
import InviteCodeModal from '../Common/InviteCodeModal';

const AppHeader = ({ bannerVisible = true }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const { user, isAuthenticated, authChecked } = useSelector((state) => state.auth);
  const { eligible } = useSelector((state) => state.invite);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  
  // 导航菜单项
  const navigationItems = [
    { label: '首页', path: '/landing' },
    // { label: '证件翻译', path: '/trans' },
    { label: '翻译认证', path: '/certification' },
    { label: '服务客户', path: '/services' },
    { label: '充值', path: '/pricing' },
    { label: '安全', path: '/security' }
  ];

  // 检查邀请码资格
  useEffect(() => {
    if (isAuthenticated && user) {
      dispatch(checkInviteEligibility()).catch(error => {
        logger.error('检查邀请码资格失败:', error);
      });
    }
  }, [isAuthenticated, user, dispatch]);

  // 获取当前选中的tab
  const getCurrentTab = () => {
    const currentPath = location.pathname;
    const index = navigationItems.findIndex(item => item.path === currentPath);
    return index >= 0 ? index : 0;
  };

  // 处理tab切换
  const handleTabChange = (event, newValue) => {
    navigate(navigationItems[newValue].path);
    // 滚动到页面顶部
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // 处理用户菜单打开
  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  // 处理用户菜单关闭
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  // 处理个人中心
  const handleProfile = () => {
    handleCloseMenu();
    navigate('/profile');
  };

  // 处理填写邀请码
  const handleInviteCode = () => {
    handleCloseMenu();
    setShowInviteModal(true);
  };
  
  // 处理退出登录
  const handleLogout = () => {
    handleCloseMenu();
    dispatch(logout());
    navigate('/landing');
  };

  // 处理登录按钮点击
  const handleLoginClick = () => {
    setShowLoginModal(true);
  };

  // 处理登录成功
  const handleLoginSuccess = (userData) => {
    logger.info('登录成功，用户数据:', userData);
    setShowLoginModal(false);
    // 这里可以根据需要跳转到特定页面
  };

  // 处理邀请码验证成功
  const handleInviteSuccess = (result) => {
    logger.info('邀请码验证成功:', result);
    // 可以在这里显示成功通知或刷新用户信息
  };
  
  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          borderBottom: 'none',
          py: 0.5,
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
          background: 'white',
          padding: 0,
          color: theme.palette.primary.main,
          top: bannerVisible ? '44px' : 0,
          transition: 'top 0.3s ease',
          zIndex: theme.zIndex.appBar
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ justifyContent: 'space-between' }}>
            {/* Logo */}
            <Box
              sx={{ 
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer'
              }}
              onClick={() => {
                navigate('/landing');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
                            {/* Logo */}
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.primary.main,
                  fontSize: '1.25rem',
                }}
              >
                                PageSmooth
              </Typography>
            </Box>

            {/* 右侧区域：导航菜单 + 用户信息/登录按钮 */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* 导航菜单 */}
              <Tabs
                value={getCurrentTab()}
                onChange={handleTabChange}
                sx={{
                  '& .MuiTabs-indicator': {
                    backgroundColor: theme.palette.primary.main,
                    height: 2,
                    borderRadius: '5px 5px 0 0',
                    boxShadow: '0 2px 8px rgba(67, 83, 255, 0.4)',
                  },
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 400,
                    fontSize: '1rem',
                    color: theme.palette.text.secondary,
                    minWidth: 80,
                    minHeight: 64,
                    padding: '12px 12px',
                    transition: 'all 0.3s ease',
                    '&.Mui-selected': {
                      color: theme.palette.primary.main,
                      fontWeight: 600
                    },
                    '&:hover': {
                      color: theme.palette.primary.main,
                      opacity: 1
                    }
                  }
                }}
              >
                {navigationItems.map((item, index) => (
                  <Tab
                    key={index}
                    label={item.label}
                    sx={{ mx: 0.5 }}
                  />
                ))}
              </Tabs>
              
              {/* 用户信息或登录按钮 */}
              {/* 认证状态确定前使用固定宽度占位，防止布局跳变 */}
              {!authChecked ? (
                <Box sx={{ display: 'flex', alignItems: 'center', paddingLeft: '20px', minWidth: '180px' }} />
              ) : isAuthenticated && user ? (
                <Box sx={{ display: 'flex', alignItems: 'center', paddingLeft: '20px' }}>
                  <Button
                    variant="contained"
                    onClick={() => navigate('/workspace')}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 2,
                      py: 0.75,
                      borderRadius: 3,
                      mr: 2,
                      boxShadow: '0 4px 12px rgba(67, 83, 255, 0.3)',
                      '&:hover': {
                        boxShadow: '0 6px 16px rgba(67, 83, 255, 0.4)',
                        transform: 'translateY(-1px)',
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    翻译工作台
                  </Button>

                  <Tooltip title="账户设置">
                    <Button
                      onClick={handleOpenMenu}
                      endIcon={<ArrowDropDownIcon sx={{ color: theme.palette.primary.main }} />}
                      sx={{
                        textTransform: 'none',
                        color: theme.palette.primary.main,
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar
                          alt={user.username}
                          src={user.avatar || ''}
                          sx={{
                            width: 32,
                            height: 32,
                            mr: 1,
                            bgcolor: theme.palette.primary.main,
                            color: 'white'
                          }}
                        >
                          {user.username.charAt(0).toUpperCase()}
                        </Avatar>
                        <Typography variant="subtitle2" sx={{ display: { xs: 'none', md: 'block' }, color: theme.palette.primary.main }}>
                          {user.username}
                        </Typography>
                      </Box>
                    </Button>
                  </Tooltip>
                  
                  <Menu
                    anchorEl={anchorEl}
                    id="account-menu"
                    open={Boolean(anchorEl)}
                    onClose={handleCloseMenu}
                    PaperProps={{
                      elevation: 2,
                      sx: {
                        overflow: 'visible',
                        filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.08))',
                        mt: 1.5,
                        borderRadius: 2,
                        minWidth: 180,
                        '& .MuiMenuItem-root': {
                          px: 2,
                          py: 1.5,
                          borderRadius: 1,
                          mx: 1,
                          my: 0.5,
                          '&:hover': {
                            backgroundColor: theme.palette.background.light,
                          },
                        },
                      },
                    }}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                  >
                    <MenuItem onClick={handleProfile}>
                      <PersonIcon sx={{ mr: 1, fontSize: 20 }} />
                      个人中心
                    </MenuItem>
                    {eligible && (
                      <MenuItem onClick={handleInviteCode}>
                        <CardGiftcardIcon sx={{ mr: 1, fontSize: 20 }} />
                        填写邀请码
                      </MenuItem>
                    )}
                    <MenuItem onClick={handleLogout}>
                      <LogoutIcon sx={{ mr: 1, fontSize: 20 }} />
                      退出登录
                    </MenuItem>
                  </Menu>
                </Box>
              ) : (
                <Box sx={{ paddingLeft: '20px' }}>
                  <Button
                    variant="contained"
                    startIcon={<LoginIcon />}
                    onClick={handleLoginClick}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 2.5,
                      py: 0.75,
                      borderRadius: 3,
                      boxShadow: '0 4px 12px rgba(67, 83, 255, 0.3)',
                      '&:hover': {
                        boxShadow: '0 6px 16px rgba(67, 83, 255, 0.4)',
                        transform: 'translateY(-1px)',
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    立即登录体验
                  </Button>
                </Box>
              )}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* 登录弹窗 */}
      <LoginModal
        open={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLoginSuccess={handleLoginSuccess}
      />

      {/* 邀请码弹窗 */}
      <InviteCodeModal
        open={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onSuccess={handleInviteSuccess}
      />
    </>
  );
};

export default AppHeader; 